import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi, tenantApi, setApiBaseUrl, clearApiReadCaches, companySettingsApi, seedMyLocationsCache } from '../services/api';
import { getCompanyDateFormat, resetCompanyDateFormat, subscribeCompanyDateFormat } from '../utils/companyDateFormat';
import { stopNotificationHub } from '../services/notificationHub';
import { loadCachedTenant, saveCachedTenant, saveLoginPreferences, snapshotCachedTenantFromSession, tenantMatchesIdentifier } from '../services/loginPreferences';
import { Employee, LoginResponse } from '../types';
import {
  LAST_SESSION_ACTIVITY_AT_KEY,
  SESSION_INACTIVITY_MS,
  touchSessionActivity,
} from '../constants/session';

interface AuthContextType {
  employee: Employee | null;
  tenantId: number | null;
  isAuthenticated: boolean;
  loading: boolean;
  /** Resolves tenant and attempts password login. Returns LoginResponse (may be MFA challenge). Only persists session when token+employee present. */
  login: (tenantIdentifier: string, email: string, password: string, options?: {
    rememberEmail?: boolean;
    resolvedTenant?: { tenantId: number; tenantCode: string; tenantName: string; apiUrl?: string | null; isActive?: boolean };
  }) => Promise<LoginResponse>;
  /** Persist authenticated session after password login or MFA completion. */
  setSession: (employee: Employee, token: string, options?: {
    tenantId?: number;
    tenantCode?: string;
    tenantName?: string;
    email?: string;
    rememberEmail?: boolean;
  }) => void;
  logout: () => Promise<void>;
  /** Update the current employee (e.g. after face re-enrollment). Persists to localStorage. */
  updateEmployee: (employee: Employee) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readStoredEmployee(): Employee | null {
  try {
    const token = localStorage.getItem('authToken');
    const savedEmployee = localStorage.getItem('employee');
    if (token && savedEmployee) {
      return JSON.parse(savedEmployee) as Employee;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [employee, setEmployee] = useState<Employee | null>(() => readStoredEmployee());
  const [loading, setLoading] = useState(true);
  const [, setCompanyDateFormat] = useState(getCompanyDateFormat);

  useEffect(() => subscribeCompanyDateFormat(() => setCompanyDateFormat(getCompanyDateFormat())), []);

  useEffect(() => {
    // Restore session if JWT + employee exist and last activity is within 14 days (same as mobile).
    const token = localStorage.getItem('authToken');
    const savedEmployee = localStorage.getItem('employee');
    const tenantApiUrl = localStorage.getItem('tenantApiUrl');

    if (token && savedEmployee) {
      try {
        const lastRaw = localStorage.getItem(LAST_SESSION_ACTIVITY_AT_KEY);
        if (lastRaw) {
          const lastMs = new Date(lastRaw).getTime();
          if (!Number.isFinite(lastMs) || Date.now() - lastMs > SESSION_INACTIVITY_MS) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('employee');
            localStorage.removeItem('tenantId');
            localStorage.removeItem('tenantApiUrl');
            localStorage.removeItem('locationsCache');
            sessionStorage.removeItem('staffly:locationsSession');
            localStorage.removeItem(LAST_SESSION_ACTIVITY_AT_KEY);
          } else {
            const employeeData = JSON.parse(savedEmployee);
            setEmployee(employeeData);
            if (tenantApiUrl) {
              setApiBaseUrl(tenantApiUrl);
            }
            touchSessionActivity();
          }
        } else {
          // Upgrade path: no marker yet — treat as valid and start tracking (same as StafflyMobile).
          const employeeData = JSON.parse(savedEmployee);
          setEmployee(employeeData);
          if (tenantApiUrl) {
            setApiBaseUrl(tenantApiUrl);
          }
          touchSessionActivity();
        }
      } catch (error) {
        console.error('Error parsing saved employee data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('employee');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('tenantApiUrl');
        localStorage.removeItem('locationsCache');
        sessionStorage.removeItem('staffly:locationsSession');
        localStorage.removeItem(LAST_SESSION_ACTIVITY_AT_KEY);
      }
    }
    if (localStorage.getItem('authToken')) {
      companySettingsApi.get().catch(() => undefined);
    }
    setLoading(false);
  }, []);

  /** Sliding activity: tab focus / visible document (keeps session alive while user uses the app in the last 14 days). */
  useEffect(() => {
    const bump = () => touchSessionActivity();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        bump();
      }
    };
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const setSession = (
    emp: Employee,
    token: string,
    options?: {
      tenantId?: number;
      tenantCode?: string;
      tenantName?: string;
      email?: string;
      rememberEmail?: boolean;
    }
  ) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('employee', JSON.stringify(emp));
    const tid = options?.tenantId ?? emp.tenantId;
    if (tid != null) {
      localStorage.setItem('tenantId', String(tid));
    }
    if (options?.tenantCode != null || options?.email != null) {
      saveLoginPreferences({
        companyCode: options.tenantCode ?? '',
        companyName: options.tenantName ?? options.tenantCode ?? '',
        email: options.email ?? emp.email ?? '',
        rememberEmail: options.rememberEmail ?? true,
        lastLoginDate: Date.now(),
      });
    }
    const apiUrl = typeof window !== 'undefined' ? localStorage.getItem('tenantApiUrl') : null;
    if (tid != null && options?.tenantCode && apiUrl) {
      saveCachedTenant({
        tenantId: tid,
        tenantCode: options.tenantCode,
        tenantName: options.tenantName,
        apiUrl,
      });
    }
    touchSessionActivity();
    if (emp.assignedLocations?.length) {
      seedMyLocationsCache(emp.assignedLocations);
    }
    setEmployee(emp);
    companySettingsApi.get().catch(() => undefined);
  };

  const login = async (
    tenantIdentifier: string,
    email: string,
    password: string,
    options?: {
      rememberEmail?: boolean;
      resolvedTenant?: { tenantId: number; tenantCode: string; tenantName: string; apiUrl?: string | null; isActive?: boolean };
    }
  ): Promise<LoginResponse> => {
    const preResolved = options?.resolvedTenant;
    const cachedTenant = loadCachedTenant();
    const tenant = preResolved?.tenantId && preResolved.apiUrl?.trim()
      ? {
          tenantId: preResolved.tenantId,
          tenantCode: preResolved.tenantCode,
          tenantName: preResolved.tenantName,
          isActive: preResolved.isActive !== false,
          apiUrl: preResolved.apiUrl,
        }
      : cachedTenant && tenantMatchesIdentifier(cachedTenant, tenantIdentifier)
        ? {
            tenantId: cachedTenant.tenantId,
            tenantCode: cachedTenant.tenantCode,
            tenantName: cachedTenant.tenantName,
            isActive: true,
            apiUrl: cachedTenant.apiUrl,
          }
        : await tenantApi.resolve(tenantIdentifier);
    if (!tenant.isActive) throw new Error('Tenant is inactive');
    const apiUrl = tenant.apiUrl?.trim();
    if (!apiUrl) throw new Error('Tenant has no ApiUrl configured in database.');
    setApiBaseUrl(apiUrl);
    saveCachedTenant({
      tenantId: tenant.tenantId,
      tenantCode: tenant.tenantCode,
      tenantName: tenant.tenantName,
      apiUrl,
    });
    const response = await authApi.login(tenant.tenantId, email, password);

    if ('mfaRequired' in response && response.mfaRequired) {
      return response;
    }

    if (!response.token || !response.employee) {
      throw new Error('Login failed');
    }

    setSession(response.employee, response.token, {
      tenantId: tenant.tenantId,
      tenantCode: tenant.tenantCode,
      tenantName: tenant.tenantName,
      email,
      rememberEmail: options?.rememberEmail ?? true,
    });
    return response;
  };

  const logout = async () => {
    // Keep last tenant for the login screen before session keys are cleared.
    snapshotCachedTenantFromSession();
    // Drop React auth state first so ProtectedRoute / UI unlock immediately.
    setEmployee(null);
    clearApiReadCaches();
    stopNotificationHub();
    sessionStorage.removeItem('staffly:permissionsCache');
    sessionStorage.removeItem('staffly:locationsSession');
    localStorage.removeItem('locationsCache');
    localStorage.removeItem('selectedLocationId');
    // Clears tokens and best-effort POSTs /auth/logout (uses current tenant API URL, 4s timeout).
    await authApi.logout();
    setApiBaseUrl(null);
    resetCompanyDateFormat();
  };

  const updateEmployee = useCallback((updated: Employee) => {
    setEmployee(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('employee', JSON.stringify(updated));
    }
  }, []);

  const tenantId = employee?.tenantId ?? (typeof window !== 'undefined' ? parseInt(localStorage.getItem('tenantId') ?? '', 10) : null);
  const value: AuthContextType = {
    employee,
    tenantId: Number.isNaN(tenantId) ? null : tenantId,
    isAuthenticated: !!employee,
    loading,
    login,
    setSession,
    logout,
    updateEmployee,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
